import React, { useState } from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AutoAwesome as WizardIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { api } from '../utils/api';
import { useNavigate } from 'react-router-dom';

function EshopManagement() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [currentEshop, setCurrentEshop] = useState(null);
  const [formData, setFormData] = useState({
    eshop_id: '',
    name: '',
    base_url: '',
    status: 'active',
    currency: 'CZK',  // Add currency field with default value
    config_json: ''
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const { data: eshops = [], isLoading: eshopsLoading, isError: eshopsError } = useQuery('eshops',
    () => api.get('/api/eshops').then(res => res.data),
    {
      onError: () => showSnackbar('Failed to load e-shops', 'error'),
    }
  );

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'warning';
      case 'error': return 'error';
      default: return 'default';
    }
  };

  const handleCreate = () => {
    setDialogMode('create');
    setCurrentEshop(null);
    setFormData({
      eshop_id: '',
      name: '',
      base_url: '',
      status: 'active',
      currency: 'CZK',  // Add currency field with default value
      config_json: ''
    });
    setOpenDialog(true);
  };

  const handleEdit = (eshop) => {
    setDialogMode('edit');
    setCurrentEshop(eshop);
    setFormData({
      eshop_id: eshop.eshop_id,
      name: eshop.name,
      base_url: eshop.base_url,
      status: eshop.status,
      currency: eshop.currency || 'CZK',  // Add currency field from e-shop data
      config_json: JSON.stringify(eshop.config_json || {}, null, 2)
    });
    setOpenDialog(true);
  };

  const mutationOptions = {
    onSuccess: () => {
      queryClient.invalidateQueries('eshops');
      setOpenDialog(false);
    },
    onError: (error) => {
      const message = error.response?.data?.detail || 'An error occurred';
      showSnackbar(message, 'error');
    }
  };

  const createEshop = useMutation(eshopData => api.post('/api/eshops', eshopData), {
    ...mutationOptions,
    onSuccess: () => {
      mutationOptions.onSuccess();
      showSnackbar('E-shop created successfully');
    },
  });

  const updateEshop = useMutation(eshopData => api.put(`/api/eshops/${currentEshop.id}`, eshopData), {
    ...mutationOptions,
    onSuccess: () => {
      mutationOptions.onSuccess();
      showSnackbar('E-shop updated successfully');
    },
  });

  const handleSave = () => {
    let config_json = {};
    if (formData.config_json.trim()) {
      try {
        config_json = JSON.parse(formData.config_json);
      } catch (e) {
        showSnackbar('Invalid JSON in config field', 'error');
        return;
      }
    }
    const eshopData = {
      ...formData,
      config_json,
      currency: formData.currency  // Explicitly include currency field
    };

    if (dialogMode === 'create') {
      createEshop.mutate(eshopData);
    } else {
      updateEshop.mutate(eshopData);
    }
  };

  const deleteEshop = useMutation(id => api.delete(`/api/eshops/${id}`), {
    ...mutationOptions,
    onSuccess: () => {
      queryClient.invalidateQueries('eshops');
      showSnackbar('E-shop deleted successfully');
    },
  });

  const handleDelete = (eshop) => {
    if (window.confirm(`Are you sure you want to delete ${eshop.name}?`)) {
      deleteEshop.mutate(eshop.id);
    }
  };


  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        E-shop Management
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="body1" color="text.secondary">
          Manage your e-shop configurations and scraping jobs
        </Typography>
        <Box>
          <Button
            variant="contained"
            startIcon={<WizardIcon />}
            onClick={() => navigate('/eshop-wizard')}
            color="primary"
            sx={{ mr: 2 }}
          >
            Add E-shop (Wizard)
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<AddIcon />}
            onClick={handleCreate}
          >
            Add E-shop (Manual)
          </Button>
        </Box>
      </Box>

      {eshopsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : eshopsError ? (
        <Alert severity="error">Could not load e-shops.</Alert>
      ) : (
        <Grid container spacing={3}>
          {[...eshops]
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((eshop) => (
              <Grid item xs={12} md={3} lg={3} key={eshop.id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="h6" component="div" noWrap>
                      {eshop.name}
                    </Typography>
                    <Chip
                      label={eshop.status}
                      color={getStatusColor(eshop.status)}
                      size="small"
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom noWrap>
                    ID: {eshop.eshop_id}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom noWrap>
                    URL: {eshop.base_url}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Created: {new Date(eshop.created_at).toLocaleDateString()}
                  </Typography>
                </CardContent>
                <CardActions>
                  <IconButton
                    size="small"
                    onClick={() => navigate(`/eshop-wizard/${eshop.id}`)}
                    color="secondary"
                    title="Edit with Wizard"
                  >
                    <WizardIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleEdit(eshop)}
                    color="primary"
                    title="Edit Manually"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDelete(eshop)} color="error">
                    <DeleteIcon />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {eshops.length === 0 && !eshopsLoading && (
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="h6" color="text.secondary">
            No e-shops configured yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Click "Add E-shop" to get started
          </Typography>
        </Box>
      )}

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogMode === 'create' ? 'Add New E-shop' : 'Edit E-shop'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="E-shop ID"
              value={formData.eshop_id}
              onChange={(e) => setFormData({ ...formData, eshop_id: e.target.value })}
              margin="normal"
              disabled={dialogMode === 'edit'}
            />
            <TextField
              fullWidth
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Base URL"
              value={formData.base_url}
              onChange={(e) => setFormData({ ...formData, base_url: e.target.value })}
              margin="normal"
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="error">Error</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal">
              <InputLabel>Currency</InputLabel>
              <Select
                value={formData.currency}
                onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
              >
                <MenuItem value="CZK">CZK (Czech Koruna)</MenuItem>
                <MenuItem value="EUR">EUR (Euro)</MenuItem>
                <MenuItem value="USD">USD (US Dollar)</MenuItem>
                <MenuItem value="GBP">GBP (British Pound)</MenuItem>
                <MenuItem value="PLN">PLN (Polish Zloty)</MenuItem>
                <MenuItem value="HUF">HUF (Hungarian Forint)</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Config JSON"
              value={formData.config_json}
              onChange={(e) => setFormData({ ...formData, config_json: e.target.value })}
              margin="normal"
              multiline
              rows={16}
              placeholder='{"key": "value"}'
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            {dialogMode === 'create' ? 'Create' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default EshopManagement;
